import { z } from 'zod';
import { CargoArticleSchema, CargoNonnegMoneySchema } from './article.js';

// Cargo registry list/summary WIRE shapes (#1228). The query/create/update
// INPUT envelopes live in `registry-io.ts`; this file carries the response
// projections (wire item, totals, indicators, summary) so neither file
// approaches the 400-line soft limit.

/**
 * @aiPurpose minimal owner ref returned on every registry row so the list /
 *   detail UI can render the responsible manager name without a second
 *   `GET /users` round-trip. `null` when the row is unassigned.
 */
export const CargoResponsibleManagerRefSchema = z
  .object({ id: z.string().uuid(), name: z.string() })
  .nullable();
export type CargoResponsibleManagerRef = z.infer<typeof CargoResponsibleManagerRefSchema>;

/**
 * @aiPurpose Cargo registry list/detail wire item (#1228). Extends the #1227
 *   `CargoArticleSchema` skeleton with the backend-derived issuance flag and
 *   the two age counters, plus the joined owner ref.
 * @aiInvariant intentionally NON-strict (no `.strict()`): #1230 layers
 *   client-payment fields onto the SAME wire item, so unknown keys must pass
 *   through rather than trigger a coordinated wire break. The derived fields
 *   (`isIssued`, `daysWithoutPayment`, `daysOnTheWay`) are computed per-request
 *   from a single captured `now` so list and detail are internally consistent.
 */
export const CargoArticleWireSchema = CargoArticleSchema.extend({
  // «Выдан» is a frontend label; the backend exposes the boolean only.
  isIssued: z.boolean(),
  daysWithoutPayment: z.number().int().nullable(),
  daysOnTheWay: z.number().int().nullable(),
  responsibleManager: CargoResponsibleManagerRefSchema,
  // Override the inherited optional raw fields to required-nullable on the wire
  // (#1606): to-wire.ts is the sole producer and always emits them.
  chinaWarehouseDispatchAmount: CargoNonnegMoneySchema.nullable(),
  chinaWarehouseInspectionAmount: CargoNonnegMoneySchema.nullable(),
  chinaWarehousePackagingAmount: CargoNonnegMoneySchema.nullable(),
  // WHY (#1606): computed per-row sum of the non-null values among the 4
  // «склад, Китай» amounts (chinaPaymentAmount + 3 warehouse fields); null
  // when all four are null. Derived in to-wire.ts, not stored.
  chinaWarehouseTotalAmount: CargoNonnegMoneySchema.nullable(),
  // WHY (#1933/#1936): list-cell projection of the payment-document review
  // state. Populated by #1936's list mapper; optional for rollout.
  clientPaymentDocCounts: z
    .object({
      pending: z.number().int().nonnegative(),
      accepted: z.number().int().nonnegative(),
      rejected: z.number().int().nonnegative(),
    })
    .optional(),
  clientPaymentAcceptedRubTotal: CargoNonnegMoneySchema.nullable().optional(),
});
export type CargoArticleWire = z.infer<typeof CargoArticleWireSchema>;

/**
 * @aiPurpose paginated `GET /cargo` envelope (#1228). Keyset cursor on
 *   `(createdAt, id)`; `nextCursor` is `null` on the last page.
 * @aiInvariant (#1608) `readOnlyMirror` reflects the backend `CARGO_READONLY_MIRROR`
 *   flag (NextKargo is the source during the migration bridge). REQUIRED, not
 *   optional: the list read is the sole producer and always emits it, so this is
 *   a non-breaking additive field. The SPA reads it to gate write controls; the
 *   backend guard is the real boundary, this is UX only.
 */
export const CargoListResponseSchema = z.object({
  items: z.array(CargoArticleWireSchema),
  nextCursor: z.string().nullable(),
  readOnlyMirror: z.boolean(),
});
export type CargoListResponse = z.infer<typeof CargoListResponseSchema>;

/**
 * @aiPurpose aggregate totals over the FULL filtered selection (`GET
 *   /cargo/summary`), ignoring pagination. Quantity totals are JS numbers
 *   (the underlying columns are dirty free-text, summed via `parseDirtyNumber`
 *   skipping unparseable values); `totalChinaPayment` crosses the wire as a
 *   `\d+\.\d{2}` money string so the `Decimal(12,2)` precision survives JSON.
 */
export const CargoTotalsSchema = z.object({
  count: z.number().int().nonnegative(),
  totalWeightKg: z.number(),
  totalVolumeM3: z.number(),
  totalPrr: z.number(),
  totalTat: z.number(),
  totalChinaPayment: CargoNonnegMoneySchema,
  // WHY (#1606): sum of all 4 china amounts (chinaPaymentAmount +
  // chinaWarehouseDispatch/Inspection/Packaging) over the filtered set; null
  // components count as 0; empty set → "0.00". Now REQUIRED: #1606 computes
  // and populates it in the summary read path.
  totalChinaWarehouseAmount: CargoNonnegMoneySchema,
});
export type CargoTotals = z.infer<typeof CargoTotalsSchema>;

// One indicator bucket: a count plus the `article` strings of the matching
// rows. `fillPercent` is present only on the "missing X" indicators where a
// completeness percentage is meaningful.
const CargoIndicatorBucketSchema = z.object({
  count: z.number().int().nonnegative(),
  articles: z.array(z.string()),
});
const CargoIndicatorFillBucketSchema = CargoIndicatorBucketSchema.extend({
  fillPercent: z.number(),
});

/**
 * @aiPurpose operational indicators over the FULL filtered selection (`GET
 *   /cargo/summary`). Each bucket is computed WITHIN the caller's visibility
 *   scope, so returning them to a manager exposes nothing beyond that
 *   manager's own rows; the admin/logistic-only display is a frontend
 *   decision, never a backend role-name branch.
 */
export const CargoIndicatorsSchema = z.object({
  paidNotIssued: CargoIndicatorBucketSchema,
  missingResponsibleManager: CargoIndicatorFillBucketSchema,
  missingPathFormat: CargoIndicatorFillBucketSchema,
  noInsurance: CargoIndicatorBucketSchema,
  // #1939: cargos with ≥1 ACTIVE client-payment document whose review was
  // rejected — count + the matching article strings, like the other buckets.
  rejectedDocument: CargoIndicatorBucketSchema,
});
export type CargoIndicators = z.infer<typeof CargoIndicatorsSchema>;

/**
 * @aiPurpose `GET /cargo/summary` envelope (#1228): totals + indicators over
 *   the same filtered selection the list endpoint paginates.
 */
export const CargoSummaryResponseSchema = z.object({
  totals: CargoTotalsSchema,
  indicators: CargoIndicatorsSchema,
});
export type CargoSummaryResponse = z.infer<typeof CargoSummaryResponseSchema>;

/**
 * @aiPurpose `GET /cargo/filter-options` envelope (#1884/#2164): the manager
 *   options for the registry filter dropdown, each with a count, plus the count
 *   of unassigned rows and `pathFormats` — route/transport-format option labels
 *   (not PII), scoped like the manager options — all scoped to the SAME
 *   visibility + business filters the list/summary reads apply.
 * @aiSecurity managers expose ONLY `{ id, name, count }` — the display name is
 *   already on every list row, so this leaks no new PII (no email/phone/role).
 *   `pathFormats` are option labels (not PII). Counts are computed WITHIN the
 *   caller's visibility scope, so a manager-scope actor sees only
 *   managers/counts reachable from its own rows.
 * @aiInvariant `pathFormats` (#2164) is optional-additive: the producer always
 *   populates it, but it is optional on the wire so pre-#2165 consumers/fixtures
 *   that omit it still validate (backward-compatible rollout).
 */
export const CargoFilterOptionsResponseSchema = z.object({
  managers: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      count: z.number().int().nonnegative(),
    }),
  ),
  unassignedCount: z.number().int().nonnegative(),
  pathFormats: z
    .array(
      z.object({
        value: z.string(),
        count: z.number().int().nonnegative(),
      }),
    )
    .optional(),
});
export type CargoFilterOptionsResponse = z.infer<typeof CargoFilterOptionsResponseSchema>;

/**
 * @aiPurpose `GET /cargo/assignable-managers` envelope (#2261): the roster the
 *   Cargo Add/Edit modals assign FROM — active users whose role classifies to
 *   the Cargo `'manager'` scope, resolved from the users directory (NOT from
 *   cargo rows), so a newly-registered manager with zero rows is assignable.
 *   Deliberately separate from `filter-options`, which stays row-derived.
 * @aiSecurity exposes ONLY `{ id, name }` per user — the same display name every
 *   list row already carries, so it leaks no new PII (no email/phone/role/active
 *   flag/passwordResetRequired). The route requires `cargo.update`; a
 *   manager-scope (read-only) actor is denied, so it can never enumerate the
 *   employee directory to them.
 */
export const CargoAssignableManagersResponseSchema = z
  .object({
    managers: z.array(
      z
        .object({
          id: z.string().uuid(),
          name: z.string(),
        })
        .strict(),
    ),
  })
  .strict();
export type CargoAssignableManagersResponse = z.infer<typeof CargoAssignableManagersResponseSchema>;
